/**
 * ExitIntentStrip — landing-page conversion last-chance bar.
 *
 * Fires when a logged-out visitor's mouse leaves the viewport from
 * the top edge (the classic "I'm heading back to the tab bar or the
 * back button" signal). Renders a dismissable banner with the
 * "free 7-day preview" CTA — same offer the hero leads with, but
 * timed to the moment of departure.
 *
 * Engagement rules:
 *   - Logged-out only — `useAuth` returns null → enabled. Logged-in
 *     visitors never see the strip even if they hit the landing
 *     surface mid-session.
 *   - One-shot per session — once shown OR dismissed, sessionStorage
 *     flag (`tlb_exit_intent_v1`) blocks subsequent fires.
 *   - 4-second grace period after mount — prevents a stray cursor
 *     bounce on slow-loading pages from firing immediately.
 *   - Only triggers when cursor leaves through the TOP of the
 *     viewport (`clientY <= 0`) — side/bottom exits are likely
 *     window-switches, not abandonment.
 *
 * Lightweight on purpose — no framer-motion / no portal / no library
 * dependencies. CSS slide-in animation only.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ArrowRight, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "tlb_exit_intent_v1";
const GRACE_MS = 4000;

export default function ExitIntentStrip() {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  // Track whether we've already armed/fired so the handler is a no-op
  // on stale events.
  const firedRef = useRef(false);

  useEffect(() => {
    // Gate — logged-in users + still-loading auth state both bypass.
    if (loading || user) return undefined;
    // Already seen this session — respect the dismissal.
    let seen = false;
    try {
      seen = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      seen = false;
    }
    if (seen) return undefined;

    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, GRACE_MS);

    const handler = (event) => {
      if (!armed || firedRef.current) return;
      // Only top-edge exits — relatedTarget is null when leaving the
      // window entirely, and clientY <= 0 means upward exit.
      const exitingUp = event.clientY <= 0 && !event.relatedTarget;
      if (!exitingUp) return;
      firedRef.current = true;
      setVisible(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* private mode — fire-and-forget */
      }
    };

    document.addEventListener("mouseout", handler);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", handler);
    };
  }, [loading, user]);

  const dismiss = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
  };

  if (!visible) return null;
  return (
    <div
      data-testid="exit-intent-strip"
      role="dialog"
      aria-label="Last chance — start your free preview"
      className="fixed top-0 inset-x-0 z-50 bg-[#4A4458] text-white border-b border-[#7C5FB0] shadow-lg animate-slide-down"
      style={{
        animation: "slideDownStrip 360ms cubic-bezier(0.22, 1, 0.36, 1) both",
      }}
    >
      <style>{`
        @keyframes slideDownStrip {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-4 flex-wrap justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Sparkles className="w-4 h-4 text-[#7C5FB0] flex-shrink-0" />
          <div className="text-sm leading-tight">
            <span className="font-display font-black tracking-tight">
              Before you go —
            </span>{" "}
            <span className="text-neutral-200">
              spin up your plan with a free 7-day preview. No card required.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/login"
            data-testid="exit-intent-cta"
            onClick={dismiss}
          >
            <Button className="rounded-none bg-[#7C5FB0] hover:bg-[#5E4689] text-white h-9 px-4 text-[10px] uppercase tracking-[0.25em] font-bold">
              Start free preview
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="exit-intent-dismiss"
            aria-label="Dismiss"
            className="text-neutral-300 hover:text-white p-1.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
